import React, { useState, useCallback, useContext, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { UI } from "@wwimmo/ui";
import { AssemblyAddDocumentsListFile } from "src/components/assemblies/documents/add-documents-modal/AddDocumentsModal";
import { useMutation } from "@apollo/client";
import { INSERT_NEWS_FILE_MANAGER } from "src/api/news";
import { InsertNewsFileManager, InsertNewsFileManagerVariables } from "src/api/generated/InsertNewsFileManager";
import { MessageType } from "src/components/notifications/Notifier";
import { RootStoreContext } from "src/stores/RootStore";
import { file_newsfiles_insert_input } from "src/api/generated/globalTypes";
import { NetworkConfig } from "src/network/NetworkConfig";
import { AssemblyDocumentsListItem } from "src/components/assemblies/documents/list-item/AssemblyDocumentsListItem";
import { useParams } from "react-router";

interface AddNewsDocumentsModalListProps {
    files: AssemblyAddDocumentsListFile[];
}

interface Params {
    newsid: string;
}

const MINIMUM_CHARACTERS_FOR_SEARCH = 3;

const AddNewsDocumentsModalListBase = (props: AddNewsDocumentsModalListProps) => {
    const { t } = useTranslation();
    const { uiStore, navStore } = useContext(RootStoreContext);
    const { files } = props;
    const { newsid } = useParams<Params>();

    const [displayedFileItems, setDisplayedFileItems] = useState<Array<AssemblyAddDocumentsListFile>>(files);
    const [searchQuery, setSearchQuery] = useState<string>("");

    const [insertNewsItemFileMutation] = useMutation<InsertNewsFileManager, InsertNewsFileManagerVariables>(
        INSERT_NEWS_FILE_MANAGER,
        {
            onCompleted: () => {
                printStatusMessage(t("screens.realestate.news_edit.save_file_success"), MessageType.INFO);
            }
        }
    );

    const printStatusMessage = useCallback(
        (message: string, msgType: MessageType) => {
            uiStore.enqueueSnackbar({
                content: message,
                options: {
                    type: msgType,
                    autoClose: msgType === MessageType.ERROR ? false : 3000
                }
            });
        },
        [uiStore]
    );

    const removeFileFromDisplayedList = useCallback(
        (fileid: string) => {
            const newlyDisplayedFileItems = displayedFileItems.filter((fileitem) => fileitem.fileId !== fileid);
            setDisplayedFileItems([...newlyDisplayedFileItems]);
        },
        [displayedFileItems]
    );

    const onSearch = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const currentSearchQuery = e.target.value;
            setSearchQuery(currentSearchQuery);

            if (currentSearchQuery.length >= MINIMUM_CHARACTERS_FOR_SEARCH && files.length > 0) {
                const foundItems = files.filter((file) => {
                    return (
                        file.fileName?.toLowerCase().includes(currentSearchQuery.toLowerCase()) ||
                        file.fileDate?.toLowerCase().includes(currentSearchQuery.toLowerCase())
                    );
                });

                setDisplayedFileItems([...foundItems]);
            } else {
                setDisplayedFileItems([...files]);
            }
        },
        [files]
    );

    const onAddDocumentToNewsItem = useCallback(
        (fileItem: AssemblyAddDocumentsListFile) => async () => {
            const insertNewsFileObjects: file_newsfiles_insert_input[] = [
                {
                    fileid: fileItem.fileId,
                    newsid: newsid,
                    roleid: 30,
                    visible: true,
                    type: 602
                },
                {
                    fileid: fileItem.fileId,
                    newsid: newsid,
                    roleid: navStore.isCondominium ? 32 : 33,
                    visible: true,
                    type: 602
                }
            ];

            await insertNewsItemFileMutation({
                variables: {
                    objects: insertNewsFileObjects
                }
            });

            uiStore.setDbNewsFiles([
                ...uiStore.dbNewsFiles,
                {
                    __typename: "file_v_newsfiles",
                    id: fileItem.fileId,
                    fileid: fileItem.fileId,
                    name: fileItem.fileName ?? ""
                }
            ]);

            removeFileFromDisplayedList(fileItem.fileId ?? "");
        },
        [removeFileFromDisplayedList, insertNewsItemFileMutation, newsid, uiStore, navStore.isCondominium]
    );

    const uniqueDisplayedFileItems = useMemo(() => {
        const seen = new Set<string>();
        return displayedFileItems.filter((f) => {
            const id = f.fileId;
            if (!id) return true; // or false, depending on your data expectations
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
        });
    }, [displayedFileItems]);

    const filesItems = uniqueDisplayedFileItems.map((fileItem) => {
        const assemblyFileLink = NetworkConfig.datafileUrl + fileItem.fileId;

        return (
            <div key={fileItem.fileId} onClick={onAddDocumentToNewsItem(fileItem)}>
                <AssemblyDocumentsListItem
                    key={fileItem.fileId}
                    fileDate={fileItem.fileDate}
                    fileId={fileItem.fileId}
                    fileName={fileItem.fileName}
                    fileLink={assemblyFileLink}
                    isDisplayedInModal={true}
                />
            </div>
        );
    });

    return (
        <div>
            <div style={{ width: "93%" }}>
                <UI.Input
                    type="search"
                    onChange={onSearch}
                    placeholder={t("labels.search").toString()}
                    value={searchQuery}
                />
            </div>
            <div className="mb-4" />
            <div>{filesItems}</div>
        </div>
    );
};

export const AddNewsDocumentsModalList = Sentry.withProfiler(observer(AddNewsDocumentsModalListBase));
